"use client";
import { AlertTriangle } from "lucide-react";
import { Meter as AriaMeter, MeterProps as AriaMeterProps } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { composeTailwindRenderProps } from "../../utils";
import { Label } from "../Field";

export interface MeterProps extends AriaMeterProps {
  /** Label displayed above the meter */
  label?: string;
  /** Size variant of the meter. `xs` is a bare track sized for a dense table cell. */
  size?: "xs" | "sm" | "md" | "lg";
  /** Visual variant based on semantic meaning. `neutral` opts out of value-derived colouring. */
  variant?: "default" | "success" | "warning" | "error" | "info" | "neutral";
  /** Whether to show warning indicator at high values */
  showWarningIndicator?: boolean;
  /** Threshold percentage for showing warning (default: 80) */
  warningThreshold?: number;
  /**
   * Render the track alone, without the label / value row.
   *
   * For callers that already print the measurement next to the meter — a table cell whose
   * column *is* the value — where the built-in row would repeat it.
   *
   * The meter still needs a name: pass `label` (promoted to `aria-label`, since the visible
   * `<Label>` is no longer rendered) or an explicit `aria-label`. A caller whose track is
   * purely decorative — or whose displayed `value` is clamped, so the meter would state a
   * number the adjacent text contradicts — should pass `aria-hidden` instead and leave the
   * printed value as the only accessible one.
   */
  hideValueText?: boolean;
  /** Additional CSS classes */
  className?: string;
}

const sizeStyles = {
  xs: {
    bar: "h-1 w-14",
    text: "text-xs",
    icon: "h-2.5 w-2.5",
  },
  sm: {
    bar: "h-1.5 w-48",
    text: "text-xs",
    icon: "h-3 w-3",
  },
  md: {
    bar: "h-2 w-64",
    text: "text-sm",
    icon: "h-4 w-4",
  },
  lg: {
    bar: "h-3 w-80",
    text: "text-base",
    icon: "h-5 w-5",
  },
};

/**
 * Meter
 *
 * Displays a measurement within a known range, with visual indicators for different value ranges.
 * Perfect for showing capacity, usage levels, scores, or any bounded measurement.
 */
export function Meter({
  label,
  size = "md",
  variant = "default",
  showWarningIndicator = true,
  warningThreshold = 80,
  hideValueText = false,
  className,
  ...props
}: MeterProps) {
  const styles = sizeStyles[size];

  /**
   * `label` normally names the meter through the rendered `<Label>`. `hideValueText` drops
   * that row, so the label would otherwise be accepted and then silently discarded, leaving
   * a meter with no accessible name at all. Promote it to `aria-label` instead — an explicit
   * `aria-label`/`aria-labelledby` from the caller still wins.
   */
  const labelFallback =
    hideValueText && label && !props["aria-label"] && !props["aria-labelledby"] ? { "aria-label": label } : {};

  return (
    <AriaMeter {...props} {...labelFallback} className={composeTailwindRenderProps(className, "flex flex-col gap-1")}>
      {({ percentage, valueText }) => {
        const isWarning = showWarningIndicator && percentage >= warningThreshold;
        const effectiveVariant = variant === "default" ? getAutomaticVariant(percentage) : variant;

        return (
          <>
            {!hideValueText && (
              <div className="flex justify-between gap-2">
                {label && <Label>{label}</Label>}
                <span className={twMerge(styles.text, isWarning ? "text-feedback-error-text" : "text-text-secondary")}>
                  {isWarning && (
                    <AlertTriangle
                      aria-label="Warning: High value"
                      className={twMerge("inline-block align-text-bottom mr-1", styles.icon)}
                    />
                  )}
                  {valueText}
                </span>
              </div>
            )}
            <div
              className={twMerge(
                "relative rounded-full bg-background-muted",
                "ring-1 ring-inset ring-border-muted",
                styles.bar
              )}
            >
              <div
                className={twMerge(
                  "absolute left-0 top-0 h-full rounded-full transition-all duration-300",
                  "forced-colors:bg-[Highlight]",
                  getMeterColor(effectiveVariant)
                )}
                style={{ width: `${Math.min(100, Math.max(0, percentage))}%` }}
                role="presentation"
                aria-hidden="true"
              />
            </div>
          </>
        );
      }}
    </AriaMeter>
  );
}

/**
 * Automatically determine variant based on percentage
 */
function getAutomaticVariant(percentage: number): MeterProps["variant"] {
  if (percentage < 50) return "success";
  if (percentage < 70) return "info";
  if (percentage < 90) return "warning";
  return "error";
}

/**
 * Get the appropriate color class based on variant
 */
function getMeterColor(variant: MeterProps["variant"]) {
  switch (variant) {
    case "success":
      return "bg-feedback-success-text";
    case "warning":
      return "bg-feedback-warning-text";
    case "error":
      return "bg-feedback-error-text";
    case "info":
      return "bg-feedback-info-text";
    case "neutral":
      return "bg-text-subtle";
    default:
      return "bg-action-brand";
  }
}
